import { useProduct } from "contexts/ProductContext";
import { useTranslation } from "react-i18next";
import { BasePanel } from "components/BasePanel";
import { FirestoreProductDescription } from "reedy-data/models";
import { useBookDescription } from "contexts/BookDescriptionContext";
import { useEffect } from "react";
import { getDefaultDescription } from "integrations/firebase/collections";
import { filterDescriptions } from "shared/constants";
import { EmptyList } from "components/empty/EmptyList";
import { Text } from "components/text";
import { useSearchParams } from "~/hooks/useSearchParams";

type GroupedDescriptions = Record<string, FirestoreProductDescription[]>;

export const DescriptionsList = () => {
  const { t } = useTranslation(["productDetails"]);
  const { product } = useProduct();
  const { selectedDescriptionHash, setSelectedDescriptionHash } =
    useBookDescription();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read description hash from URL on mount and when URL changes
  useEffect(() => {
    const hashFromUrl = searchParams.get("descriptionHash");
    const tabFromUrl = searchParams.get("tab");
    const defaultDescription = getDefaultDescription(product);
    const productHasDescriptionWithHash = product?.descriptions?.some(
      (desc) => desc.hash === hashFromUrl,
    );

    // Only update URL when description tab is active.
    // This prevents race conditions with tab navigation where DescriptionsList
    // could overwrite the tab param before it's committed to the URL.
    const isDescriptionTabActive = tabFromUrl === "description";

    // If there's a hash in the URL and it exists in the product descriptions, use it
    if (hashFromUrl && productHasDescriptionWithHash) {
      if (hashFromUrl !== selectedDescriptionHash) {
        setSelectedDescriptionHash(hashFromUrl);
      }
    } else if (defaultDescription?.hash) {
      // Otherwise use the default description if available
      const { hash } = defaultDescription;
      setSelectedDescriptionHash(hash);

      // Only update URL if description tab is active to prevent race conditions
      if (isDescriptionTabActive) {
        setSearchParams(
          (currentParams) => {
            currentParams.set("descriptionHash", hash);
            return currentParams;
          },
          { replace: true },
        );
      }
    }
  }, [
    searchParams,
    setSelectedDescriptionHash,
    selectedDescriptionHash,
    product,
    setSearchParams,
  ]);

  // Update URL when description hash changes
  const handleDescriptionClick = (hash: string) => {
    if (hash) {
      setSelectedDescriptionHash(hash);
      // Use callback form to ensure we're working with current URL params
      setSearchParams(
        (currentParams) => {
          currentParams.set("descriptionHash", hash);
          return currentParams;
        },
        { replace: true },
      );
    }
  };

  const filteredDescriptions = filterDescriptions(product?.descriptions ?? []);

  // Group descriptions by textType
  const groupedDescriptions = filteredDescriptions.reduce<GroupedDescriptions>(
    (acc, desc) => {
      const textType =
        desc.textType || t("productDetails:bookDescription.defaultTextType");
      if (!acc[textType]) {
        acc[textType] = [];
      }
      acc[textType].push(desc);
      return acc;
    },
    {},
  );

  return (
    <BasePanel
      title={t("productDetails:bookDescription.textTypes")}
      isEmpty={!product?.descriptions?.length}
      emptyComponent={
        <EmptyList
          title={t("productDetails:bookDescription.noTextTypes")}
          text={t("productDetails:bookDescription.noTextTypesDescription")}
          variant="primary"
        />
      }
      className="h-full"
      border={false}
    >
      <div>
        <div className="space-y-6">
          {Object.entries(groupedDescriptions || {}).map(
            ([textType, descriptions]) => (
              <div key={textType} className="space-y-2">
                <Text as="h3" variant="body" className="pl-2 pt-4">
                  {textType}
                </Text>
                <div className=" space-y-2">
                  {descriptions.map((description) => (
                    <button
                      type="button"
                      key={description.hash}
                      onClick={() =>
                        description.hash &&
                        handleDescriptionClick(description.hash)
                      }
                      className={`flex items-center space-x-2 text-md text-secondary-800 transition-colors w-full text-left p-2 ${
                        selectedDescriptionHash === description.hash
                          ? "border-l-2 border-primary-500 bg-secondary-50"
                          : "border-l-2 border-transparent"
                      }`}
                    >
                      <span className="ml-6">
                        {description.audiences?.join(", ") || "-"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </BasePanel>
  );
};
